/* ===========================================================================
 * TEMPORARY — Batch 1 production import panel
 * ---------------------------------------------------------------------------
 * Two-button workflow:
 *   1. Run Dry-Run   → calls POST /api/admin/epstein/batch1/dry-run
 *   2. Apply Import  → calls POST /api/admin/epstein/batch1/apply
 *
 * Token + plan_hash are captured from the dry-run response and held in
 * component state. Apply is disabled until a successful dry-run exists.
 * A confirmation modal sits between Apply and the network call.
 *
 * Items land non-public (`public=false`, `lifecycle_status="review"`).
 * No publish option is exposed.
 *
 * REMOVE this file (and its import in AdminEpstein.jsx) after Batch 1 import
 * has been verified in the Lifecycle Review bucket and the backend endpoints
 * have been removed.
 * ===========================================================================
 */
import React, { useState } from "react";
import {
  AlertTriangle, Loader2, Play, Upload, CheckCircle2, X, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { efAdminBatch1DryRun, efAdminBatch1Apply } from "../../lib/api";

const StatBox = ({ label, value, intent }) => (
  <div className="border border-[#2D2A26] bg-[#0a0907] px-3 py-2"
       data-testid={`b1-stat-${label}`}>
    <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#757065]">{label}</div>
    <div className={"font-display text-2xl " + (
      intent === "warn" ? "text-[#E0A526]" :
      intent === "danger" ? "text-[#D92D20]" :
      intent === "ok" ? "text-[#5DD4B3]" : "text-[#E4DFD3]"
    )}>{value}</div>
  </div>
);

export const AdminBatch1Panel = ({ token, userToken = null }) => {
  const [dryRun, setDryRun] = useState(null);
  const [dryRunBusy, setDryRunBusy] = useState(false);
  const [applyBusy, setApplyBusy] = useState(false);
  const [applyResult, setApplyResult] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const runDryRun = async () => {
    setDryRunBusy(true);
    setApplyResult(null);
    try {
      const r = await efAdminBatch1DryRun(token, userToken);
      setDryRun(r);
      toast.success(
        `Dry-run OK — ${r.plan.sources_to_create.length} sources, ` +
        `${r.plan.entities_to_create.length} entities, ` +
        `${r.plan.relationships_to_create.length} relationships, ` +
        `${r.plan.skipped.length} skipped`,
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Dry-run failed");
    } finally {
      setDryRunBusy(false);
    }
  };

  const runApply = async () => {
    if (!dryRun) return;
    setApplyBusy(true);
    setConfirmOpen(false);
    try {
      const r = await efAdminBatch1Apply(token, {
        dry_run_token: dryRun.dry_run_token,
        plan_hash:     dryRun.plan_hash,
        confirm:       true,
      }, userToken);
      setApplyResult(r);
      // Invalidate the used token — dry-run must be re-run before next apply
      setDryRun(null);
      toast.success(
        `Imported — Δ entities ${r.delta.ef_entities >= 0 ? "+" : ""}${r.delta.ef_entities}, ` +
        `relationships ${r.delta.ef_relationships >= 0 ? "+" : ""}${r.delta.ef_relationships}, ` +
        `sources ${r.delta.ef_sources >= 0 ? "+" : ""}${r.delta.ef_sources}`,
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Apply failed");
    } finally {
      setApplyBusy(false);
    }
  };

  return (
    <div data-testid="ef-admin-batch1-panel">
      {/* TEMPORARY banner */}
      <div className="border border-[#E0A526]/50 bg-[#1a1408] px-3 py-2 mb-3 flex items-start gap-2"
           data-testid="b1-temporary-banner">
        <AlertTriangle size={14} className="text-[#E0A526] mt-0.5 shrink-0" />
        <div className="text-[11px] text-[#E4DFD3] leading-relaxed">
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#E0A526]">
            TEMPORARY TOOLING ·
          </span>{" "}
          This panel exists to import Batch 1 (Original Case) on production where there is no
          terminal. It will be removed (file + endpoints) after the import succeeds and the
          Lifecycle Review bucket has been verified.
        </div>
      </div>

      <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
        <div>
          <div className="font-mono text-[10px] tracking-[0.3em] text-[#D92D20] mb-2 flex items-center gap-2">
            <Upload size={13} /> // BATCH 1 IMPORT (PRODUCTION)
          </div>
          <h2 className="font-display text-2xl text-[#E4DFD3]">Batch 1 — Original Case</h2>
          <p className="text-[#A39E93] mt-1 text-sm max-w-3xl">
            Reads <code className="text-[#CBB892]">/app/memory/epstein_batch_1_real.json</code> on
            the live server and projects the missing entities, relationships, and sources onto the
            target DB. Items land non-public (<b className="text-[#E0A526]">lifecycle_status=review</b>);
            promotion still requires the existing lifecycle UI.
          </p>
        </div>
      </div>

      {/* Step 1 — Dry-run */}
      <div className="border border-[#2D2A26] bg-[#0F0F0F] p-3 mb-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20] flex items-center gap-2">
            <Play size={11} /> // STEP 1 · DRY-RUN (READ ONLY)
          </div>
          <button
            data-testid="b1-run-dryrun"
            onClick={runDryRun}
            disabled={dryRunBusy}
            className="bg-[#D92D20] hover:bg-[#B92318] disabled:opacity-40 text-white px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] flex items-center gap-2">
            {dryRunBusy ? <Loader2 size={11} className="animate-spin" /> : <Play size={11} />}
            {dryRun ? "Re-run Dry-Run" : "Run Dry-Run"}
          </button>
        </div>

        {dryRun && (
          <div className="mt-3 space-y-3" data-testid="b1-dryrun-result">
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              <StatBox label="sources to create"  value={dryRun.plan.sources_to_create.length}  intent="ok" />
              <StatBox label="entities to create" value={dryRun.plan.entities_to_create.length} intent="ok" />
              <StatBox label="relationships to create" value={dryRun.plan.relationships_to_create.length} intent="ok" />
              <StatBox label="skipped" value={dryRun.plan.skipped.length} intent="warn" />
              <StatBox label="warnings" value={dryRun.plan.warnings.length} intent="warn" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <StatBox label="ef_entities (before)"      value={dryRun.before.ef_entities.total} />
              <StatBox label="ef_relationships (before)" value={dryRun.before.ef_relationships.total} />
              <StatBox label="ef_sources (before)"       value={dryRun.before.ef_sources.total} />
            </div>

            {dryRun.plan.warnings?.length > 0 && (
              <div className="border border-[#2D2A26] bg-[#0a0907] p-3">
                <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#E0A526] mb-2">
                  // WARNINGS ({dryRun.plan.warnings.length})
                </div>
                <ul className="font-mono text-[10px] text-[#E4DFD3] space-y-1">
                  {dryRun.plan.warnings.map((w, i) => (
                    <li key={i} className="break-all">! {w}</li>
                  ))}
                </ul>
              </div>
            )}

            <details className="border border-[#2D2A26] bg-[#0a0907] p-3"
                     data-testid="b1-skipped-section">
              <summary className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#A39E93] cursor-pointer">
                // SKIPPED ({dryRun.plan.skipped.length}) — click to expand
              </summary>
              <ul className="font-mono text-[10px] text-[#A39E93] mt-2 space-y-1">
                {dryRun.plan.skipped.map((s, i) => (
                  <li key={i} className="break-all">
                    ~ [{s.kind}] {s.reason}
                    {s.ref && <span className="text-[#757065]"> · ref={s.ref}</span>}
                    {s.existing_id && <span className="text-[#757065]"> · existing={s.existing_id}</span>}
                    {s.existing_has_image && <span className="text-[#E0A526]"> · HAS IMAGE — preserved</span>}
                  </li>
                ))}
              </ul>
            </details>

            <details className="border border-[#2D2A26] bg-[#0a0907] p-3">
              <summary className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#A39E93] cursor-pointer">
                // RAW PLAN JSON — click to expand
              </summary>
              <pre className="font-mono text-[9px] text-[#E4DFD3] mt-2 overflow-auto max-h-[320px] whitespace-pre-wrap">
{JSON.stringify(dryRun.plan, null, 2)}
              </pre>
            </details>

            <div className="font-mono text-[9px] text-[#757065]">
              token=<span className="text-[#A39E93]">{dryRun.dry_run_token.slice(0, 12)}…</span>
              {" · "}hash=<span className="text-[#A39E93]">{dryRun.plan_hash}</span>
              {" · "}expires <span className="text-[#A39E93]">{new Date(dryRun.expires_at).toLocaleString()}</span>
            </div>
          </div>
        )}
      </div>

      {/* Step 2 — Apply */}
      <div className="border border-[#2D2A26] bg-[#0F0F0F] p-3 mb-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20] flex items-center gap-2">
            <Upload size={11} /> // STEP 2 · APPLY (NON-PUBLIC, lifecycle=review)
          </div>
          <button
            data-testid="b1-run-apply"
            onClick={() => setConfirmOpen(true)}
            disabled={!dryRun || applyBusy}
            className="bg-[#D92D20] hover:bg-[#B92318] disabled:opacity-40 text-white px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] flex items-center gap-2">
            {applyBusy ? <Loader2 size={11} className="animate-spin" /> : <Upload size={11} />}
            Apply Import
          </button>
        </div>
        {!dryRun && (
          <div className="font-mono text-[10px] text-[#757065] mt-2">
            Run Step 1 first. Apply is disabled until a successful dry-run exists.
          </div>
        )}

        {applyResult && (
          <div className="mt-3 space-y-2" data-testid="b1-apply-result">
            <div className="border border-[#5DD4B3]/40 bg-[#0a1612] px-3 py-2 flex items-start gap-2">
              <CheckCircle2 size={14} className="text-[#5DD4B3] mt-0.5 shrink-0" />
              <div className="text-[11px] text-[#E4DFD3] leading-relaxed">
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#5DD4B3]">
                  IMPORT APPLIED ·
                </span>{" "}
                Items inserted non-public. <b>Verify them in the Lifecycle tab → Review bucket</b>{" "}
                before promoting to public.
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              <StatBox label="entities Δ"
                       value={(applyResult.delta.ef_entities >= 0 ? "+" : "") + applyResult.delta.ef_entities}
                       intent="ok" />
              <StatBox label="relationships Δ"
                       value={(applyResult.delta.ef_relationships >= 0 ? "+" : "") + applyResult.delta.ef_relationships}
                       intent="ok" />
              <StatBox label="sources Δ"
                       value={(applyResult.delta.ef_sources >= 0 ? "+" : "") + applyResult.delta.ef_sources}
                       intent="ok" />
              <StatBox label="entities (now)"      value={applyResult.after.ef_entities.total} />
              <StatBox label="relationships (now)" value={applyResult.after.ef_relationships.total} />
              <StatBox label="sources (now)"       value={applyResult.after.ef_sources.total} />
            </div>

            <div className="font-mono text-[10px] text-[#A39E93]">
              backfilled source_refs on {applyResult.backfilled_existing_entities || 0} pre-existing
              entities (additive only · images preserved)
            </div>

            <div className="border border-[#E0A526]/40 bg-[#1a1408] px-3 py-2 flex items-start gap-2">
              <Trash2 size={14} className="text-[#E0A526] mt-0.5 shrink-0" />
              <div className="text-[11px] text-[#E4DFD3] leading-relaxed">
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#E0A526]">
                  CLEANUP NEXT ·
                </span>{" "}
                After you verify the Review bucket, ask me to remove this panel + the backend
                endpoints, then redeploy.
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Confirm modal */}
      {confirmOpen && dryRun && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-[2px] flex items-start justify-center overflow-y-auto py-10 px-4"
          onClick={(e) => { if (e.target === e.currentTarget) setConfirmOpen(false); }}
          data-testid="b1-confirm-modal"
        >
          <div className="border border-[#D92D20]/50 bg-[#0F0F0F] w-full max-w-lg">
            <div className="px-4 py-3 border-b border-[#2D2A26] flex items-center justify-between">
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20] flex items-center gap-2">
                <AlertTriangle size={12} /> // CONFIRM BATCH 1 APPLY
              </div>
              <button onClick={() => setConfirmOpen(false)} data-testid="b1-confirm-close"
                className="text-[#A39E93] hover:text-[#D92D20]"><X size={16} /></button>
            </div>
            <div className="p-4 space-y-3">
              <div className="text-[12px] text-[#E4DFD3] leading-relaxed">
                You are about to insert{" "}
                <b className="text-[#5DD4B3]">{dryRun.plan.sources_to_create.length} sources</b>,{" "}
                <b className="text-[#5DD4B3]">{dryRun.plan.entities_to_create.length} entities</b>,{" "}
                and{" "}
                <b className="text-[#5DD4B3]">{dryRun.plan.relationships_to_create.length} relationships</b>{" "}
                into the live database.
              </div>
              <div className="text-[11px] text-[#A39E93] leading-relaxed">
                All items land <b className="text-[#E0A526]">non-public</b> (
                <code className="text-[#CBB892]">public=false</code>,{" "}
                <code className="text-[#CBB892]">lifecycle_status=&quot;review&quot;</code>).
                Promotion still requires the existing Lifecycle workflow.
                Existing records and images are preserved.
              </div>
              <div className="font-mono text-[9px] text-[#757065]">
                hash={dryRun.plan_hash} · token={dryRun.dry_run_token.slice(0, 12)}…
              </div>
            </div>
            <div className="px-4 py-3 border-t border-[#2D2A26] flex items-center justify-end gap-2">
              <button onClick={() => setConfirmOpen(false)}
                data-testid="b1-confirm-cancel"
                className="border border-[#454038] hover:border-[#D92D20] text-[#A39E93] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em]">
                Cancel
              </button>
              <button onClick={runApply} disabled={applyBusy}
                data-testid="b1-confirm-apply"
                className="bg-[#D92D20] hover:bg-[#B92318] disabled:opacity-40 text-white px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] flex items-center gap-2">
                {applyBusy ? <Loader2 size={11} className="animate-spin" /> : <Upload size={11} />}
                Yes, Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBatch1Panel;
